import { z } from "zod";
import {
  ExternalCallUrl,
  LESSON_DURATION_MAX,
  LESSON_DURATION_MIN,
  LocalDate,
  LocalTime,
  MyOpening,
  TimeZoneName,
} from "./schedule.ts";

/**
 * Самозапись ученика на открытое время: контракты ручек
 * (`docs/booking-design.md`).
 *
 * Схемы ответа сетки (`MyOpening`, `BookableOpening`) живут в `./schedule.ts`
 * рядом с `ScheduleResponse`, а не здесь: этот файл берёт оттуда `LocalDate` и
 * прочее, и обратный импорт замкнул бы цикл модулей, то есть `ReferenceError`
 * на инициализации схемы zod.
 */

export const OpeningId = z.string().uuid();
export type OpeningId = z.infer<typeof OpeningId>;

/**
 * Потолок пачки «каждую неделю до». Больше это отказ `too_many_openings`, а не
 * молчаливая обрезка: преподаватель должен узнать, что до выбранной даты окна
 * не дошли.
 */
export const BOOKING_BATCH_MAX = 60;

/**
 * Открыть время для записи.
 *
 * ⚠️ Момент считает СЕРВЕР из даты, времени и зоны, как у разового занятия
 * (`CreateSingleLessonRequest`): зона приезжает своим полем, потому что
 * человек набирает время, глядя на календарь в своей зоне.
 *
 * `repeatUntil` заводит ПАЧКУ независимых окон на тот же день недели до даты
 * включительно. Это не серия: у пачки нет правила, только общая метка.
 */
export const CreateOpeningsRequest = z.object({
  date: LocalDate,
  time: LocalTime,
  timeZone: TimeZoneName,
  durationMin: z.number().int().min(LESSON_DURATION_MIN).max(LESSON_DURATION_MAX),
  /**
   * Ссылка на созвон, копируется в урок при записи. ⚠️ Та же схема, что у урока:
   * иначе запись на окно падала бы пятисоткой на `CHECK` урока.
   */
  externalCallUrl: ExternalCallUrl.nullable().optional(),
  repeatUntil: LocalDate.nullable().optional(),
});
export type CreateOpeningsRequest = z.infer<typeof CreateOpeningsRequest>;

/**
 * Почему окно пачки не открыто.
 *
 * - `teacher_busy`: у преподавателя в это время занятие
 * - `already_open`: пересекается с другим открытым окном (в том числе
 *   созданным параллельно, это ловит база)
 * - `no_such_time`: местного времени в этот день нет (перевод часов)
 * - `past`: время уже прошло
 */
export const OpeningSkipReason = z.enum(["teacher_busy", "already_open", "no_such_time", "past"]);
export type OpeningSkipReason = z.infer<typeof OpeningSkipReason>;

/**
 * Исход создания. Пропущенное названо преподавателю прямо: пачка поверх его
 * недели публикует дырку, и видеть её он обязан (1.4 проекта).
 */
export const CreateOpeningsResponse = z.object({
  created: z.array(MyOpening),
  skipped: z.array(z.object({ date: LocalDate, reason: OpeningSkipReason })),
});
export type CreateOpeningsResponse = z.infer<typeof CreateOpeningsResponse>;
